// src/Features/Projects/services/projectService.ts
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from "firebase/firestore";
import { db } from "@/config/firebase";
import type {
  Project,
  CreateProjectData,
  UpdateProjectData,
  ProjectStatus,
  ProjectPhase,
} from "@/features/Projects";

class ProjectService {
  private readonly collection = collection(db, "ai_projects");

  // Criar novo projeto
  async create(data: CreateProjectData): Promise<string> {
    try {
      // ✅ Projeto criado com descrição final processada pela IA
      const projectData = {
        name: data.name,
        description: data.description, // Descrição já processada pela IA
        currentPhase: "description" as ProjectPhase, // Inicia na fase de descrição
        status: "draft" as ProjectStatus,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const docRef = await addDoc(this.collection, projectData);
      return docRef.id;
    } catch (error) {
      console.error("Erro ao criar projeto:", error);
      throw new Error("Falha ao criar projeto");
    }
  }

  // Buscar projeto por ID
  async getById(id: string): Promise<Project> {
    try {
      const docRef = doc(this.collection, id);
      const snapshot = await getDoc(docRef);

      if (!snapshot.exists()) {
        throw new Error("Projeto não encontrado");
      }

      const data = snapshot.data();
      
      
      return {
        id: snapshot.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as Project;
    } catch (error) {
      console.error("Erro ao buscar projeto:", error);
      throw new Error("Falha ao carregar projeto");
    }
  }

  // ✅ ATUALIZADO - Listar TODOS os projetos (sem filtro por usuário)
  async getByUser(userId: string): Promise<Project[]> {
    try {

      // Query simples - todos os projetos ordenados por data
      const q = query(this.collection, orderBy("updatedAt", "desc"));

      const snapshot = await getDocs(q);


      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Project;
      });
    } catch (error) {
      console.error("Erro ao listar projetos:", error);
      throw new Error("Falha ao carregar projetos");
    }
  }

  // Listar projetos recentes
  async getRecent(limitCount: number = 10): Promise<Project[]> {
    try {
      const q = query(
        this.collection,
        orderBy("createdAt", "desc"),
        limit(limitCount)
      );

      const snapshot = await getDocs(q);

      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Project;
      });
    } catch (error) {
      console.error("Erro ao buscar projetos recentes:", error);
      throw new Error("Falha ao carregar projetos recentes");
    }
  }

  // Listar projetos por status
  async getByStatus(status: ProjectStatus): Promise<Project[]> {
    try {
      const q = query(
        this.collection,
        where("status", "==", status),
        orderBy("updatedAt", "desc")
      );

      const snapshot = await getDocs(q);

      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Project;
      });
    } catch (error) {
      console.error("Erro ao buscar projetos por status:", error);
      throw new Error("Falha ao carregar projetos");
    }
  }

  // Sanitizar dados para Firebase
  private sanitizeForFirebase(data: any): any {
    if (data === null || data === undefined) {
      return data;
    }
    
    if (Array.isArray(data)) {
      return data.map(item => this.sanitizeForFirebase(item));
    }
    
    if (typeof data === 'object') {
      const sanitized: any = {};
      for (const [key, value] of Object.entries(data)) {
        // Remove funções e valores undefined
        if (typeof value !== 'function' && value !== undefined) {
          sanitized[key] = this.sanitizeForFirebase(value);
        }
      }
      return sanitized;
    }
    
    return data;
  }

  // Atualizar projeto
  async update(id: string, data: UpdateProjectData): Promise<void> {
    try {
      const docRef = doc(this.collection, id);
      
      
      const sanitizedData = this.sanitizeForFirebase(data);
      
      
      await updateDoc(docRef, {
        ...sanitizedData,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error("Erro ao atualizar projeto:", error);
      console.error("Dados originais:", data);
      throw error instanceof Error ? error : new Error("Falha ao atualizar projeto");
    }
  }

  // Atualizar fase do projeto
  async updatePhase(id: string, phase: ProjectPhase): Promise<void> {
    try {
      await this.update(id, { currentPhase: phase });
    } catch (error) {
      console.error("Erro ao atualizar fase do projeto:", error);
      throw new Error("Falha ao atualizar fase do projeto");
    }
  }

  // Atualizar status do projeto
  async updateStatus(id: string, status: ProjectStatus): Promise<void> {
    try {
      await this.update(id, { status });
    } catch (error) {
      console.error("Erro ao atualizar status do projeto:", error);
      throw new Error("Falha ao atualizar status do projeto");
    }
  }


  // Deletar projeto
  async delete(id: string): Promise<void> {
    try {
      const docRef = doc(this.collection, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error("Erro ao deletar projeto:", error);
      throw new Error("Falha ao deletar projeto");
    }
  }

  // ✅ ATUALIZADO - Buscar projetos por termo (sem filtro de usuário)
  async search(searchTerm: string, userId?: string): Promise<Project[]> {
    try {
      // Buscar todos os projetos (ignorando userId já que não temos createdBy)
      const q = query(this.collection, orderBy("name"));
      const snapshot = await getDocs(q);

      const allProjects = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Project;
      });

      // Filtrar pelo termo de busca
      const filteredProjects = allProjects.filter(
        (project) =>
          project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          project.description
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      );

      return filteredProjects;
    } catch (error) {
      console.error("Erro ao buscar projetos:", error);
      throw new Error("Falha na busca de projetos");
    }
  }

  // ✅ ATUALIZADO - Estatísticas gerais (não por usuário)
  async getUserStats(userId: string): Promise<{
    total: number;
    byStatus: Record<ProjectStatus, number>;
    byPhase: Record<ProjectPhase, number>;
  }> {
    try {
      // Buscar todos os projetos em vez de filtrar por usuário
      const projects = await this.getByUser(userId); // Ainda usa getByUser mas agora retorna todos

      const byStatus: Record<ProjectStatus, number> = {
        draft: 0,
        planning: 0,
        in_progress: 0,
        completed: 0,
        cancelled: 0,
      };

      const byPhase: Record<ProjectPhase, number> = {
        initial_description: 0,
        expanded_description: 0,
        objectives: 0,
        technical_solution: 0,
        milestones: 0,
        stories: 0,
        schema: 0,
        resources: 0,
        proposal: 0,
        execution: 0,
      };

      projects.forEach((project) => {
        byStatus[project.status]++;
        byPhase[project.currentPhase]++;
      });

      return {
        total: projects.length,
        byStatus,
        byPhase,
      };
    } catch (error) {
      console.error("Erro ao buscar estatísticas:", error);
      throw new Error("Falha ao carregar estatísticas");
    }
  }
}

export const projectService = new ProjectService();
